"use client"

import { ControlledModal, toast } from "@/components"
import { InstagramFlow } from "@/components/instagramFlow/InstagramFlow"
import { useInstagramFlowStore } from "@/components/instagramFlow/instagramStore"
import { Button, IconButton, RadioButton, Separator, Text, TextInput } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { FetchModalsAndAboutDocument, UpdateCreativeContactInfoDocument } from "@/gql/graphql"
import { SocialAccountProvider } from "@/types"
import { PREFERS_URL_OVER_USERNAME } from "@/utils/socialAccounts"
import { useApolloClient, useMutation } from "@apollo/client"
import { PlusIcon } from "@heroicons/react/24/outline"
import { captureException } from "@sentry/nextjs"
import Link from "next/link"
import { FC, useEffect, useState } from "react"

const EditContactInfoModalFragment = graphql(`
  fragment EditContactInfoModal on IndividualCreative {
    __typename
    id
    contactEmail
    website
    vimeoUrl
    linkedinUsername
    twitterUsername
    instagramUsername
    dribbbleUsername

    user {
      id
      __typename
      socialAccounts {
        edges {
          node {
            __typename
            id
            provider
            profileUrl
            username
            verified
          }
        }
      }
    }
  }
`)

graphql(`
  mutation UpdateCreativeContactInfo($input: UpdateCreativeContactInfoInput!) {
    updateContactInfo: updateCreativeContactInfo(input: $input) {
      creative {
        id
        __typename
        ... on IndividualCreative {
          website
          contactEmail
          instagramUsername
          twitterUsername
          vimeoUrl
          linkedinUsername
          dribbbleUsername
        }
      }

      errors {
        field
        messages
      }
    }
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof EditContactInfoModalFragment> | null | undefined
}

const getProviderUri = (provider: SocialAccountProvider, currentPath: string, state: string) => {
  const vimeoRedirectUri = `${window.location.origin}/auth/vimeo`
  switch (provider) {
    case "vimeo":
      return `https://api.vimeo.com/oauth/authorize?response_type=code&client_id=${process.env.NEXT_PUBLIC_VIMEO_CLIENT_ID!}&redirect_uri=${encodeURIComponent(vimeoRedirectUri)}&state=${state}`
    case "instagram":
      return `https://api.instagram.com/oauth/authorize?client_id=${process.env.NEXT_PUBLIC_INSTAGRAM_CLIENT_ID!}&redirect_uri=${encodeURIComponent(currentPath)}`
    //case "dribbble":
    //  return `https://dribbble.com/oauth/authorize?client_id=${process.env.NEXT_PUBLIC_DRIBBBLE_CLIENT_ID!}&redirect_uri=${process.env.NEXT_PUBLIC_DRIBBBLE_REDIRECT_URI!}&state=${state}&scope=public`
    // case "linkedin":
    // return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${process.env.NEXT_PUBLIC_LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(currentPath)}&state=${state}&scope=profile%20email%20openid`
  }
}

export const EditContactInfoModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const [update] = useMutation(UpdateCreativeContactInfoDocument)
  const creative = useFragment(EditContactInfoModalFragment, props.creative)
  const [loading, setLoading] = useState(false)
  const redirectUrl = window.location.href + "&openContactModal=true"

  const socialAccounts = creative?.user?.socialAccounts?.edges

  const [website, setWebsite] = useState(creative?.website || "")
  const [contactEmail, setContactEmail] = useState(creative?.contactEmail || "")
  const [vimeoUrl, setVimeoUrl] = useState(creative?.vimeoUrl || "")
  const [linkedinUsername, setLinkedinUsername] = useState(creative?.linkedinUsername || "")
  const [twitterUsername, setTwitterUsername] = useState(creative?.twitterUsername || "")
  const [instagramUsername, setInstagramUsername] = useState(creative?.instagramUsername || "")
  const [dribbble, setDribbble] = useState(creative?.dribbbleUsername || "")

  const onUpdate = async () => {
    if (loading) return
    setLoading(true)
    const response = await update({
      variables: {
        input: {
          id: creative?.id!,
          contactEmail,
          website,
          instagramUsername,
          twitterUsername: twitterUsername.replace("x.com/", ""),
          vimeoUrl,
          linkedinUsername: linkedinUsername.replace("linkedin.com/", ""),
          dribbble,
        },
      },
    })

    const errors = response.data?.updateContactInfo?.errors

    if (errors && errors.length > 0) {
      console.log(errors)
      toast.error("Error updating contact info")
      captureException(errors)
    } else {
      onOpenChange(false)
    }

    setLoading(false)
  }

  const vimeoSocialAccounts = socialAccounts?.filter(account => account?.node.provider === "vimeo")
  const instagramSocialAccounts = socialAccounts?.filter(account => account?.node.provider.startsWith("instagram"))

  const inputs = [
    { label: "Website URL", value: website, onChange: setWebsite, prefix: "", placeholder: "Your website" },
    { label: "Email", value: contactEmail, onChange: setContactEmail, prefix: "", placeholder: "Your email" },
    {
      label: "X",
      value: twitterUsername,
      onChange: setTwitterUsername,
      prefix: "x.com/",
      placeholder: "Your X username",
    },
    {
      label: "LinkedIn",
      value: linkedinUsername,
      onChange: setLinkedinUsername,
      prefix: "linkedin.com/",
      placeholder: "Your LinkedIn username",
    },

    // {
    //   label: "Dribbble",
    //   value: dribbble,
    //   onChange: setDribbble,
    //   prefix: "dribbble.com/",
    //   placeholder: "Your Dribbble username",
    // },
  ]

  useEffect(() => {
    if (creative) {
      setWebsite(creative.website || "")
      setContactEmail(creative.contactEmail || "")
      setVimeoUrl(creative.vimeoUrl)
      setLinkedinUsername(creative.linkedinUsername || "")
      setTwitterUsername(creative.twitterUsername || "")
      setInstagramUsername(creative.instagramUsername || "")
      setDribbble(creative.dribbbleUsername || "")
    }
  }, [creative])

  const [instagramFlowOpen, setInstagramFlowOpen] = useState(false)
  const [instagramFlowSocialId, setInstagramFlowSocialId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!isOpen) {
      setInstagramFlowOpen(false)
      setInstagramFlowSocialId(undefined)
    }
  }, [isOpen])

  const client = useApolloClient()

  useEffect(() => {
    let channel: BroadcastChannel
    if ("BroadcastChannel" in window) {
      channel = new BroadcastChannel("social_account_connected")

      channel.onmessage = event => {
        if (event.data.type === "vimeo") {
          client.refetchQueries({
            include: [FetchModalsAndAboutDocument],
          })
        }
      }
    }

    return () => {
      if (channel) {
        channel.close()
      }
    }
  }, [client])

  const { footer: instagramFooter, header: instagramHeader } = useInstagramFlowStore()

  return (
    <>
      <ControlledModal
        zIndex={102}
        open={instagramFlowOpen}
        onOpenChange={() => {
          setInstagramFlowOpen(false)
          setInstagramFlowSocialId(undefined)
        }}
        header={instagramHeader}
        footer={instagramFooter}
        body={
          <InstagramFlow
            refetchQueries={[FetchModalsAndAboutDocument]}
            instagramFlowSocialId={instagramFlowSocialId}
            closeFlow={() => {}}
            onVerifyCallback={(value: string) => {
              setInstagramUsername(value)
            }}
          />
        }
      />
      <ControlledModal
        open={isOpen}
        onOpenChange={onOpenChange}
        header={{ title: "Edit your contact info", variant: "default", fixed: true }}
        body={
          <>
            <div className="flex w-full flex-col overflow-hidden pt-5 md:pt-6">
              <>
                <div className="flex flex-col gap-8 overflow-y-scroll">
                  {inputs.map(input => {
                    return (
                      <TextInput
                        key={input.label}
                        label={input.label}
                        tabIndex={0}
                        placeholder={input.placeholder}
                        value={input.prefix + input.value.replace(input.prefix, "")}
                        onChange={e => {
                          if (e.target.value.length >= input.prefix.length) {
                            const value = e.target.value.replace(input.prefix, "")
                            input.onChange(value)
                          }
                        }}
                      />
                    )
                  })}
                  <div>
                    <div className="mb-3">
                      <Text variant="label">Vimeo</Text>
                    </div>
                    {vimeoSocialAccounts?.map((account, index) => {
                      const provider = account?.node?.provider
                      const prefersUrlOverUsername = PREFERS_URL_OVER_USERNAME.includes(
                        provider as SocialAccountProvider
                      )
                      const value = prefersUrlOverUsername ? account?.node.profileUrl : account?.node.username
                      const checked = value === vimeoUrl

                      return (
                        <div key={index} className="pb-2">
                          <div
                            className="flex cursor-pointer flex-row items-center justify-between pb-2"
                            onClick={() => {
                              if (checked) {
                                setVimeoUrl("")
                              } else {
                                setVimeoUrl(value!)
                              }
                            }}>
                            <Text>{value?.replace("https://", "")}</Text>
                            <RadioButton name={provider} value={value || ""} checked={checked} />
                          </div>
                          <Separator />
                        </div>
                      )
                    })}
                    <div className="flex flex-row items-center justify-between pb-2">
                      <Text>
                        Add {vimeoSocialAccounts && vimeoSocialAccounts.length > 0 ? "another" : "an"} account
                      </Text>
                      <Link
                        target="_blank"
                        href={getProviderUri("vimeo", redirectUrl, Math.random().toString(36).substring(7)) || ""}>
                        <IconButton Icon={PlusIcon} />
                      </Link>
                    </div>
                  </div>
                  <div>
                    <div className="mb-3">
                      <Text variant="label">Instagram</Text>
                    </div>
                    {instagramSocialAccounts?.map((account, index) => {
                      const provider = account?.node?.provider
                      const prefersUrlOverUsername = PREFERS_URL_OVER_USERNAME.includes(
                        provider as SocialAccountProvider
                      )
                      const value = prefersUrlOverUsername ? account?.node.profileUrl : account?.node.username
                      const checked = value === instagramUsername

                      return (
                        <div key={index} className="pb-2">
                          <div
                            className="flex cursor-pointer flex-row items-center justify-between pb-2"
                            onClick={() => {
                              if (checked) {
                                setInstagramUsername("")
                              } else {
                                setInstagramUsername(value!)
                              }
                            }}>
                            <Text>{value}</Text>
                            {account?.node.provider === "instagram_nova" && account.node.verified === false ? (
                              <Button
                                onClick={() => {
                                  setInstagramFlowOpen(true)
                                  setInstagramFlowSocialId(account?.node.id)
                                }}
                                variant="tertiary"
                                className="cursor-pointer text-fg-2">
                                Verify
                              </Button>
                            ) : (
                              <RadioButton name={provider} value={value || ""} checked={checked} />
                            )}
                          </div>
                          <Separator />
                        </div>
                      )
                    })}
                    <div className="flex flex-row items-center justify-between pb-2">
                      <Text>
                        Add {instagramSocialAccounts && instagramSocialAccounts.length > 0 ? "another" : "an"} account
                      </Text>
                      <IconButton Icon={PlusIcon} onClick={() => setInstagramFlowOpen(true)} />
                    </div>
                  </div>
                </div>
              </>
            </div>
          </>
        }
        footer={{
          fixed: true,
          tertiaryButton: {
            children: "Cancel",
            onClick: () => onOpenChange(false),
          },
          primaryButton: {
            children: "Update",
            onClick: onUpdate,
            loading: loading,
          },
        }}
      />
    </>
  )
}
